import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';
import { FixedSizeList } from 'react-window';
import '../template.module.css';
import { ReportTemplatesContext } from '..';

export default function CustomSymbolizationModal({
  Section,
  setModal,
  setDefaultMapFlag,
  setBlock,
}) {
  const {
    fetchLayerUrlsAndTitles,
    fetchToken,
    fetchFields,
    getAccessToken,
    addCustomMapInfo,
  } = useContext(ReportTemplatesContext);

  const [modeSelected, setModeSelected] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [localLoader, setLocalLoader] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null); // will be one of the layer names
  const [localBlocks, setLocalBlocks] = useState([]);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [pendingBlock, setPendingBlock] = useState(null); // Stores the block pending confirmation

  const handleBlockSelection = useCallback(block => {
    setPendingBlock(block);
    setConfirmationDialogOpen(true); // Open the dialog when a block is selected
  }, []);

  const handleConfirmation = boundariesOnly => {
    setBlock({ ...pendingBlock, boundaries: boundariesOnly });
    addCustomMapInfo(Section.key, {
      ...pendingBlock,
      boundaries: boundariesOnly,
    });
    setModal(false);
    setConfirmationDialogOpen(false);
    setPendingBlock(null);
  };

  const blocksToShow = localBlocks.filter(
    block =>
      !block.status &&
      block.title.toLowerCase().includes(searchText.toLowerCase()),
  );

  async function getGroupsForMapServer(mapserverURL) {
    try {
      console.log('Getting Groups for Map server...');
      const response = await fetch(
        `${process.env.REACT_APP_NODE_URL}/map/groups`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAccessToken()}`,
          },
          body: JSON.stringify({
            mapserverURL: mapserverURL,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Couldn't fetch the layers");
      }

      const data = await response.json();
      console.log({ data });

      if (!data.layers) {
        throw new Error('Layers data is missing from the response');
      }

      console.log({ layers: data.layers });

      return data.layers;
    } catch (error) {
      console.error(error);
      throw error; // Re-throw the error to handle it in the calling function
    }
  }

  async function localGroupsFetcherRoutine() {
    try {
      setLocalLoader(true);
      const token = await fetchToken();

      const layerUrlsAndTitles = await fetchLayerUrlsAndTitles(
        Section.map.webmapID,
        token,
      );

      if (!layerUrlsAndTitles) {
        console.log('layerUrlsAndTitles is empty');
        return;
      }

      console.log({ layerUrlsAndTitles });
      let mapServerURL = null;
      layerUrlsAndTitles.forEach(layer => {
        if (layer?.url.includes('/MapServer')) {
          const match = layer.url.match(/.*\/MapServer\//);
          if (match) {
            mapServerURL = match[0];
          }
        }
      });

      if (mapServerURL) {
        console.log(mapServerURL);
        const groupNamesAndIDs = await getGroupsForMapServer(mapServerURL);

        const groupUrlsAndNames = groupNamesAndIDs.map(group => {
          return {
            url: `${mapServerURL}${group.id}`,
            title: group.name,
          };
        });

        console.log({ groupUrlsAndNames });
        setGroups(groupUrlsAndNames);
      } else {
        const layers = layerUrlsAndTitles.map(layer => layer.url);
        const newBlocks = await fetchFields(layers, token);
        if (!newBlocks) {
          throw new Error('newBlocks is empty');
        }
        console.log({ newBlocks });
        setLocalBlocks(newBlocks);
        setSelectedGroup('None');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLocalLoader(false);
    }
  }

  async function localBlocksFetcherRoutine() {
    try {
      setLocalLoader(true);
      const token = await fetchToken();

      const layerUrls = [];
      layerUrls.push(selectedGroup.url);
      const newBlocks = await fetchFields(layerUrls, token);
      if (!newBlocks) {
        throw new Error('newBlocks is empty');
      }
      console.log({ newBlocks });
      setLocalBlocks(newBlocks);
    } catch (error) {
      console.error(error);
    } finally {
      setLocalLoader(false);
    }
  }

  useEffect(() => {
    if (!modeSelected) return;
    localGroupsFetcherRoutine();
  }, [modeSelected]);

  useEffect(() => {
    if (!selectedGroup || selectedGroup === 'None') return;
    localBlocksFetcherRoutine();
  }, [selectedGroup]);

  return (
    <Box
      sx={{
        zIndex: 3,
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {/* Actual Modal */}
      <Box
        sx={{
          width: '40.5rem',
          height: '35.25rem',
          backgroundColor: '#FFFFFF',
          borderRadius: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.85rem',
        }}
      >
        {/* Heading + Close Button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#F9F9F9',
            padding: '1.5rem',
            borderTopLeftRadius: '1rem',
            borderTopRightRadius: '1rem',
          }}
        >
          {/* Heading */}
          <Typography
            sx={{
              fontFamily: 'Lato',
              fontSize: '1rem',
              fontWeight: 700,
              color: '#004852',
            }}
          >
            Custom Symbolization
          </Typography>
          {/* Close Button */}
          <Box
            sx={{ cursor: 'pointer' }}
            onClick={() => {
              setModal(false);
            }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
              className='lucide lucide-x'
            >
              <path d='M18 6 6 18' />
              <path d='m6 6 12 12' />
            </svg>
          </Box>
        </Box>

        {!modeSelected && (
          <Box>
            <Typography
              sx={{
                fontFamily: 'Lato',
                fontSize: '1rem',
                fontWeight: 700,
                color: '#004852',
                marginLeft: '1rem',
              }}
            >
              Select Map Mode
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                marginTop: '5rem',
                gap: '2rem',
                alignItems: 'left',
              }}
            >
              <Box
                sx={{
                  width: '10rem',
                  marginLeft: '1rem',
                  paddingX: '2rem',
                  paddingY: '1rem',
                  border: '2px solid #004852',
                  borderRadius: '0.75rem',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setDefaultMapFlag();
                  setModeSelected(true);
                  setModal(false);
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Lato',
                    fontSize: '1rem',
                    fontWeight: 700,
                    color: '#004852',
                  }}
                >
                  Default Map
                </Typography>
              </Box>

              <Box
                sx={{
                  width: '10rem',
                  marginLeft: '1rem',
                  paddingX: '2rem',
                  paddingY: '1rem',
                  border: '2px solid #004852',
                  borderRadius: '0.75rem',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setModeSelected(true);
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Lato',
                    fontSize: '1rem',
                    fontWeight: 700,
                    color: '#004852',
                  }}
                >
                  Custom
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {modeSelected &&
          (selectedGroup === null ? (
            localLoader === true ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '5rem',
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                  overflowY: 'auto',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Lato',
                    fontSize: '1rem',
                    fontWeight: 700,
                    color: '#004852',
                    marginLeft: '1rem',
                  }}
                >
                  Pick a group to fetch its blocks
                </Typography>
                <Box>
                  {groups.map(group => {
                    return (
                      <Box
                        key={group.title}
                        sx={{
                          paddingY: '0.5rem',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          sx={{
                            marginLeft: '1rem',
                            fontFamily: 'Lato',
                            fontSize: '1rem',
                            fontWeight: 700,
                            width: '22rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {group.title}
                        </Typography>
                        <Button
                          sx={{
                            backgroundColor: '#4FA490',
                            height: '3.5rem',
                            width: '14rem', // Fixed width
                            color: '#FFFFFF',
                            padding: '0.5rem 1.25rem',
                            borderRadius: '0.75rem',
                            '&:hover': {
                              backgroundColor: '#4FA490',
                            },
                            marginRight: '1rem',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                          startIcon={<AddCircleOutlineIcon />}
                          onClick={() => {
                            console.log({ group });
                            setSelectedGroup(group);
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Lato',
                              fontSize: '1rem',
                              fontWeight: 700,
                            }}
                          >
                            Select Group
                          </Typography>
                        </Button>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )
          ) : (
            <>
              {/* Search Bar */}
              <Box sx={{ marginX: '1.5rem' }}>
                <input
                  type='text'
                  id='blockName'
                  name='blockName'
                  placeholder='Search for Data Blocks'
                  style={{
                    boxSizing: 'border-box',
                    height: '3rem',
                    width: '100%',
                    padding: '1rem',
                    border: '1px solid #E2E8F0',
                    borderRadius: '0.75rem',
                  }}
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                />
              </Box>

              {/* Virtualized Block List */}
              {localLoader === true ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: '5rem',
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <FixedSizeList
                  height={400}
                  itemCount={blocksToShow.length}
                  itemSize={70}
                  width={'100%'}
                  style={{
                    display: 'flex',
                  }}
                >
                  {({ index, style }) => {
                    const block = blocksToShow[index];
                    return (
                      <Box
                        key={block.title}
                        sx={{
                          ...style,
                          paddingY: '0.5rem',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          sx={{
                            marginLeft: '1rem',
                            fontFamily: 'Lato',
                            fontSize: '1rem',
                            fontWeight: 700,
                            width: '22rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {block.title}
                        </Typography>
                        <Button
                          sx={{
                            backgroundColor: '#4FA490',
                            height: '3.5rem',
                            width: '14rem', // Fixed width
                            color: '#FFFFFF',
                            padding: '0.5rem 1.25rem',
                            borderRadius: '0.75rem',
                            '&:hover': {
                              backgroundColor: '#4FA490',
                            },
                            marginRight: '1rem',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                          startIcon={<AddCircleOutlineIcon />}
                          onClick={() => handleBlockSelection(block)}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Lato',
                              fontSize: '1rem',
                              fontWeight: 700,
                            }}
                          >
                            Map Data Block
                          </Typography>
                        </Button>
                      </Box>
                    );
                  }}
                </FixedSizeList>
              )}
            </>
          ))}

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmationDialogOpen}
          onClose={() => setConfirmationDialogOpen(false)}
        >
          <DialogTitle>Select Display Mode</DialogTitle>
          <DialogActions>
            <Button onClick={() => handleConfirmation(true)}>
              Boundaries Only
            </Button>
            <Button onClick={() => handleConfirmation(false)}>
              Full Map Data
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
